import { FormattedValue } from '@e2m-customer-portal/ui';
import { Tooltip as BaseTooltip, TooltipProps } from '@mui/material';
import clsx from 'clsx';
import { forwardRef, ReactElement, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { FeedInIcon, InfoIcon } from '../../../../../assets/icons';

export interface AnnualOverviewRowProps {
  latestFeedDate?: Date | number | null;
  monthData?: (number | null | undefined)[];
  yearSummary?: number | string | null;
  rowTitle: string | ReactNode;
  unit?: string;
  color?: string;
  placeholderMode?: 'month' | 'year';
  fractionDigits?: number;
  contextInfo?: string;
  yearSummaryTooltip?: string;
  disableMonthTooltip?: boolean;
}

const Tooltip = forwardRef(({ children, ...props }: TooltipProps, ref): ReactElement => {
  const tooltipProps = {
    arrow: true,
    placement: 'top' as TooltipProps['placement'],
    ...props,
  };

  return (
    <BaseTooltip {...tooltipProps}>
      <div ref={ref as RefObject<HTMLDivElement>}>{children}</div>
    </BaseTooltip>
  );
});

export function AnnualOverviewRow({
  latestFeedDate,
  monthData,
  yearSummary,
  rowTitle,
  unit,
  color,
  placeholderMode,
  fractionDigits = 0,
  contextInfo,
  yearSummaryTooltip,
  disableMonthTooltip = false,
}: Readonly<AnnualOverviewRowProps>): ReactElement<AnnualOverviewRowProps> {
  const { t } = useTranslation();

  return (
    <>
      <div className="sticky left-0 flex items-center justify-between border-0 border-b border-r border-solid border-grey-200 bg-white py-2 pl-6 pr-4 text-left text-sm shadow-lg shadow-grey-200 md:shadow-none">
        {color?.length && <FeedInIcon className="absolute left-2 w-2" style={{ color }} />}
        <div className="relative w-full inline-flex items-center justify-between gap-x-2 overflow-hidden text-ellipsis pr-2 font-semibold">
          <span>{rowTitle}</span>
          {contextInfo && (
            <Tooltip title={contextInfo} placement="top" className="flex items-center">
              <InfoIcon className="w-4 text-green-200" />
            </Tooltip>
          )}
        </div>
        {unit ? <div className="w-[2.5ch] border-0 border-l border-solid border-grey-200 px-2">{unit}</div> : null}
      </div>

      {monthData?.length
        ? monthData.map((data, index) => (
            <div
              className={clsx(
                'flex min-w-6 items-center justify-end border-0 border-b border-l border-solid border-grey-200 px-4 py-2 text-right text-sm',
                {
                  'border-l-0': !index,
                },
              )}
              key={`${rowTitle}-${index}-${data}`}
            >
              <Tooltip
                title={
                  disableMonthTooltip
                    ? null
                    : latestFeedDate
                      ? t('MARKET_LOCATION_FEED_IN_ANNUAL_OVERVIEW_ROW_TOOLTIP', { date: latestFeedDate })
                      : t('NO_DATA')
                }
              >
                <FormattedValue
                  className="text-sm font-normal tabular-nums"
                  fractionDigits={fractionDigits}
                  hideUnit
                  value={data}
                />
              </Tooltip>
            </div>
          ))
        : (!placeholderMode || placeholderMode === 'year') && (
            <div
              className="col-span-12 max-w-full border-0 border-b border-solid border-grey-200 bg-white"
              data-testid="grid-year-summary-only"
            />
          )}

      {placeholderMode === 'month' &&
        new Array(12).fill(0).map((_, index) => (
          <div
            className={clsx(
              'flex min-w-6 items-center justify-end border-0 border-b border-l border-solid border-grey-200 px-4 py-2 text-right text-sm',
              {
                'border-l-0': !index,
              },
            )}
            key={`${rowTitle}-${index}`}
          >
            <div className="flex w-full items-center justify-center gap-1">
              <div className="animate-loading-1 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
              <div className="animate-loading-2 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
              <div className="animate-loading-3 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
            </div>
          </div>
        ))}

      <div
        className="flex items-center justify-end border-0 border-b border-l border-solid border-grey-200 bg-white py-2 pl-4 pr-6 text-right text-sm"
        data-testid="grid-year-cell"
      >
        {placeholderMode ? (
          <div className="flex w-full items-center justify-center gap-1">
            <div className="animate-loading-1 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
            <div className="animate-loading-2 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
            <div className="animate-loading-3 aspect-square w-1.5 animate-pulse rounded-full bg-black/10" />
          </div>
        ) : (
          <Tooltip title={yearSummaryTooltip} data-testid="year-tooltip">
            <FormattedValue
              className="text-sm font-semibold tabular-nums"
              value={yearSummary}
              hideUnit
              fractionDigits={fractionDigits}
            />
          </Tooltip>
        )}
      </div>
    </>
  );
}
